<template>
  <button
    :type="nativeType"
    class="btn"
    :class="[`btn-${color}`, { long: long }, { smart: smart }, { outline: outline }]"
    :disabled="disabled || loading"
    @click="handleClick"
  >
    <i class="fas fa-spinner fa-pulse mr-1" v-if="loading"></i>
    <i :class="icon" v-if="icon && !loading" class="mr-1"></i>
    <span v-if="$slots.default"><slot></slot></span>
  </button>
</template>

<script>
export default {
  name: 'Button',
  props: {
    nativeType: {
      type: String,
      default: 'button',
    },
    color: {
      type: String,
      default: 'white',
    },
    icon: {
      type: String,
      default: '',
    },
    loading: Boolean,
    disabled: Boolean,
    plain: Boolean,
    long: Boolean,
    smart: Boolean,
    outline: Boolean,
  },
  methods: {
    handleClick(evt) {
      this.$emit('click', evt);
    },
  },
};
</script>

<style lang="scss" scoped>
.btn {
  height: 35px;
  margin-top: 1px;
  margin-bottom: 1px;
  padding: 0.5rem 0.9rem;
  font-size: 0.9rem;
  text-align: center;
  white-space: nowrap;
  vertical-align: middle;
  user-select: none;
  cursor: pointer;
  border: 2px solid transparent;
  border-radius: 0.25rem;
  transition: all 0.2s ease-in-out;
}
.long {
  width: 100%;
}
.smart {
  padding: 0.375rem 0.75rem;
  height: initial;
}

//Color
.btn-primary {
  color: #fff;
  background-color: #047bf8;
  border-color: #047bf8;
  &:hover {
    background-color: #0362c6;
    border-color: #035dbc;
  }
  &:active {
    background-color: #0362c6;
    border-color: #035dbc;
  }
  &:focus {
    box-shadow: 0 0 0 2px rgba(4, 123, 248, 0.5);
  }
  &.outline {
    color: #047bf8;
    border-color: #047bf8;
    &:hover,
    &:active {
      color: #fff;
      background-color: #047bf8;
      border-color: #047bf8;
    }
    &:focus {
      box-shadow: 0 0 0 2px rgba(4, 123, 248, 0.5);
    }
  }
}

.btn-secondary {
  color: #fff;
  background-color: #19213c;
  border-color: #19213c;
  &:hover,
  &:active {
    background-color: #0a0d18;
    border-color: #070911;
  }

  &:focus {
    box-shadow: 0 0 0 2px rgba(25, 33, 60, 0.5);
  }
  &.outline {
    color: #19213c;
    border-color: #19213c;
    &:hover,
    &:active {
      color: #fff;
      background-color: #19213c;
      border-color: #19213c;
    }
    &:focus {
      box-shadow: 0 0 0 2px rgba(25, 33, 60, 0.5);
    }
  }
}

.btn-info {
  color: #fff;
  background-color: #5bc0de;
  border-color: #5bc0de;
  &:hover,
  &:active {
    background-color: #31b0d5;
    border-color: #2aabd2;
  }
  &:focus {
    box-shadow: 0 0 0 2px rgba(91, 192, 222, 0.5);
  }
  &.outline {
    color: #5bc0de;
    border-color: #5bc0de;
    &:hover,
    &:active {
      color: #fff;
      background-color: #5bc0de;
      border-color: #5bc0de;
    }
    &:focus {
      box-shadow: 0 0 0 2px rgba(91, 192, 222, 0.5);
    }
  }
}

.btn-success {
  color: #fff;
  background-color: #579d1b;
  border-color: #579d1b;
  &:hover {
    background-color: #3f7114;
    border-color: #3a6912;
  }
  &:active {
    background-color: #3f7114;
    border-color: #3a6912;
  }
  &:focus {
    box-shadow: 0 0 0 2px rgba(87, 157, 27, 0.5);
  }
  &.outline {
    color: #579d1b;
    border-color: #579d1b;
    &:hover,
    &:active {
      color: #fff;
      background-color: #579d1b;
      border-color: #579d1b;
    }
    &:focus {
      box-shadow: 0 0 0 2px rgba(91, 192, 222, 0.5);
    }
  }
}

.btn-warning {
  color: #5a5033;
  background-color: #f9f1d9;
  border-color: #f9f1d9;
  &:hover {
    background-color: #f2e1ad;
    border-color: #f1dea4;
  }
  &:active {
    background-color: #f2e1ad;
    border-color: #f1dea4;
  }
  &:focus {
    box-shadow: 0 0 0 2px rgba(249, 241, 217, 0.5);
  }
  &.outline {
    color: #f9f1d9;
    border-color: #f9f1d9;
    &:hover,
    &:active {
      color: #fff;
      background-color: #f9f1d9;
      border-color: #f9f1d9;
    }
    &:focus {
      box-shadow: 0 0 0 2px rgba(249, 241, 217, 0.5);
    }
  }
}

.btn-danger {
  color: #fff;
  background-color: #e65252;
  border-color: #e65252;
  &:hover {
    background-color: #e02525;
    border-color: #db2020;
  }
  &:active {
    background-color: #e02525;
    border-color: #db2020;
  }
  &:focus {
    box-shadow: 0 0 0 2px rgba(230, 82, 82, 0.5);
  }
  &.outline {
    color: #e65252;
    border-color: #e65252;
    &:hover,
    &:active {
      color: #fff;
      background-color: #e65252;
      border-color: #e65252;
    }
    &:focus {
      box-shadow: 0 0 0 2px rgba(230, 82, 82, 0.5);
    }
  }
}

.outline {
  background-color: transparent;
}
</style>
